import { useHistory, useParams } from 'react-router-dom';
import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { useToast } from '@givebutter/primitives/toast';
import { fetchCampaigns } from 'api/campaigns';
import { uploadFile } from 'api/media';
import { createOrUpdateWidget } from 'api/widgets';
import { WidgetFormValues, transformWidgetTypeToReadable } from 'components/form/WidgetForm';
import { useGivebutterForm } from 'components/form/useGivebutterForm';
import { UploadFile } from 'components/upload';
import { useDeleteWidgetMutation } from 'components/widgets/DeleteWidgetModal';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useCurrentWidget } from 'customHooks/useCurrentWidget';
import { useDefaultColor } from 'customHooks/useDefaultColor';
import ButtonImg from 'images/widgets-button.svg?react';
import DonationFormImg from 'images/widgets-form.svg?react';
import GoalBarImg from 'images/widgets-goal-bar.svg?react';
import { accountQueryKeys } from 'queries/account-query-keys';
import { widgetQueryKeys } from 'queries/widget-query-keys';
import { AccountSetting, Campaign, Widget, WidgetType } from 'types/domain';

const CAMPAIGN_LIMIT = 100;

export interface UseWidgetFormProps {
    defaultValues?: UseFormOptions['defaultValues'];
}

export const useAccountsWidgetForm = (props: UseWidgetFormProps = {}) => {
    const toast = useToast();
    const { defaultValues } = props;
    const queryClient = useQueryClient();
    const history = useHistory();
    const params = useParams<{ widgetId: string }>();
    const { accountId, settings } = useCurrentAccount();
    const returnUrl = `/accounts/${accountId}/settings/integrations/widgets`;
    const {
        isSuccess: didDelete,
        isPending: isDeleting,
        mutate: deleteWidget,
    } = useDeleteWidgetMutation({
        widgetId: parseInt(params.widgetId),
        onSuccess: () => history.push(returnUrl),
    });
    const { data: widget } = useCurrentWidget({ enabled: !didDelete && !isDeleting });
    const widgetId = widget?.id ? widget.id : undefined;

    const { data: campaigns = [] } = useSuspenseQuery({
        queryKey: accountQueryKeys.campaigns(accountId),
        queryFn: () => fetchCampaigns(accountId, { limit: CAMPAIGN_LIMIT, excl_type: 'external' }),
        select: campaignMeta => campaignMeta.data,
    });

    const { methods, ...restFormState } = useFormBootstrap({
        widget,
        campaigns,
        settings,
        defaultValues,
    });
    const { setError, clearErrors, handleSubmit } = methods;

    const { isPending: isLoading, mutate: createOrUpdateWidgetMutation } = useMutation({
        mutationFn: createOrUpdateWidget,
        onError: () => {
            setError('widgetType', { type: 'manual' });
        },
        onSuccess: widgetFromResponse => {
            queryClient.setQueryData(
                widgetQueryKeys.widget(accountId, widgetFromResponse.id),
                widgetFromResponse,
            );
            if (!widgetId) {
                history.push(`${returnUrl}/${widgetFromResponse.id}/embed`);
            } else {
                toast.success('Widget updated successfully');
                history.push(returnUrl);
            }

            queryClient.invalidateQueries({ queryKey: widgetQueryKeys.widgets(accountId) });
        },
    });

    const onSubmit = async (values: WidgetFormValues) => {
        const widgetConfig = transformWidgetFormValuesIntoRequest(values);

        const image = methods.getValues('image')?.[0] as UploadFile | undefined;

        let nextImage: string | null = null;
        try {
            if (image) {
                const { data } = await uploadFile(image.file);
                nextImage = data.url;
            }
        } catch (e) {}

        createOrUpdateWidgetMutation({
            accountId,
            widgetId,
            widgetConfig: {
                ...widgetConfig,
                config: {
                    ...widgetConfig.config,
                    image: nextImage,
                },
            },
        });
    };

    const onClickSubmit = () => {
        clearErrors();
    };

    return {
        ...restFormState,
        returnUrl,
        methods,
        accountId,
        widget: widget,
        isEditing: !!widget,
        globalDisabled: isLoading,
        handleSubmit,
        deleteWidget,
        onSubmitUnwrapped: onSubmit,
        onSubmit: handleSubmit(onSubmit),
        onClickSubmit,
    };
};

interface UseFormOptions {
    widget: Widget | null;
    campaigns: Campaign[];
    settings: AccountSetting[];
    defaultValues?: Partial<WidgetFormValues>;
}

const useFormBootstrap = ({ widget, campaigns, settings, defaultValues }: UseFormOptions) => {
    const defaultColor = useDefaultColor();
    const defaultCampaign = getDefaultCampaign(campaigns);

    const possibleWidget: Widget = widget ?? ({} as Widget);

    const hydratedWidget: Partial<Widget> = {
        ...possibleWidget,
        campaign: widget?.campaign ?? defaultCampaign ?? null,
        code: '',
        config: {
            ...(possibleWidget.config ? possibleWidget.config : {}),
            label: widget?.config?.label || defaultValues?.label,
            backgroundColor: widget?.config?.backgroundColor || defaultColor,
            icon: widget?.config?.icon || defaultValues?.icon,
            iconPosition: widget?.config?.iconPosition || defaultValues?.iconPosition,
            dropShadow: widget?.config?.dropShadow ?? defaultValues?.dropShadow,
        },
        name: widget?.name || defaultValues?.widgetName,
        type: widget?.type || defaultValues?.widgetType,
    };

    const methods = useGivebutterForm<WidgetFormValues>({
        defaultValues: transformWidgetIntoFormValues(hydratedWidget as Widget),
    });

    const { watch } = methods;

    const watchWidgetType = watch('widgetType');
    const watchBorderWidth = watch('borderWidth');
    const isButton = watchWidgetType === 'button';
    const isButtonTypeFloating = watch('type') !== 'static';
    const isSignupForm = watchWidgetType === 'signup-form';
    const isGivingForm = watchWidgetType === 'giving-form';
    const isGoalBarTypeProgressBar = watch('type') === 'progress-bar';
    const isGoalBarThermometerType = watch('type') === 'thermometer';
    const isSignupFormTypePopup = watch('type') === 'popup';
    const isSignupFormNonStaticLayout = watch('layout') !== 'simple';
    const showConfig = typeof watchWidgetType !== 'undefined';

    return {
        watchBorderWidth,
        isButton,
        isButtonTypeFloating,
        isSignupForm,
        isGivingForm,
        isGoalBarTypeProgressBar,
        isGoalBarThermometerType,
        isSignupFormTypePopup,
        isSignupFormNonStaticLayout,
        showConfig,
        methods,
    };
};

const fallbackWidgetType = 'button';
export const getNewWidgetProps = (widget?: Widget) => {
    const widgetProps = {
        Svg: widgetImageFactory(fallbackWidgetType),
        name: transformWidgetTypeToReadable(fallbackWidgetType),
        installText: widgetInstructionsFactory(fallbackWidgetType),
    };

    if (widget) {
        widgetProps.Svg = widgetImageFactory(widget.type);
        widgetProps.name = transformWidgetTypeToReadable(widget.type);
        widgetProps.installText = widgetInstructionsFactory(widget.type);
    }

    return widgetProps;
};

const inHtmlInstructions =
    'Copy the following snippet and put it in your HTML where you would like your widget to be rendered.';
export const widgetInstructionsFactory = (widgetType: WidgetType) => {
    switch (widgetType) {
        case 'button':
        case 'giving-form':
        case 'signup-form':
        case 'goal-bar':
        default:
            return inHtmlInstructions;
    }
};

export const widgetImageFactory = (widgetType: WidgetType) => {
    switch (widgetType) {
        case 'giving-form':
            return DonationFormImg;
        case 'goal-bar':
            return GoalBarImg;
        case 'button':
        default:
            return ButtonImg;
    }
};

interface TransformedWidgetFormValues extends Partial<Widget> {
    campaign_id: number;
}
export const transformWidgetFormValuesIntoRequest = (
    values: WidgetFormValues,
): TransformedWidgetFormValues => {
    return {
        campaign_id: parseInt(values.campaignId),
        type: values.widgetType,
        name: values.widgetName,
        status: values.status,
        config: {
            label: values.label,
            textColor: values.textColor,
            size: values.size,
            type: values.type,
            progressBarColor: values.progressBarColor,
            backgroundColor: values.backgroundColor,
            borderColor: values.borderColor,
            borderRadius: values.borderRadius[0].toString(),
            borderWidth: values.borderWidth[0].toString(),
            labelColor: values.labelColor,
            position: values.type === 'floating' ? values.position : undefined,
            icon: values.icon,
            iconPosition: values.iconPosition,
            dropShadow: values.dropShadow,
            showGoalAmount: values.showGoalAmount,
            showRaisedAmount: values.showRaisedAmount,
            showRaisedPercentage: values.showRaisedPercentage,
            showGoalBar: values.showGoalBar,
            title: values.title,
            description: values.description,
            channel: values.channel ?? undefined,
            buttonText: values.buttonText,
            buttonBackgroundColor: values.buttonBackgroundColor,
            buttonTextColor: values.buttonTextColor,
            openDelay: values.openDelay[0].toString(),
            layout: values.layout ?? undefined,
            image: values.image?.[0]?.url ?? null,
            imageSizing: values.imageSizing,
            isFirstAndLastNameRequired: values.isFirstAndLastNameRequired,
        },
    };
};

const transformWidgetIntoFormValues = (widget: Widget): WidgetFormValues => ({
    campaignId: widget.campaign?.id?.toString() ?? '',
    widgetType: widget?.type ?? undefined,
    layout: widget.config?.layout ?? 'simple',
    widgetName: widget?.name ?? '',
    title: widget?.config?.title ?? 'Subscribe to our newsletter',
    description: widget?.id
        ? (widget?.config?.description ?? '')
        : 'Join our newsletter and get the latest updates.',
    textColor: widget?.config?.textColor ?? '',
    size: widget?.config?.size ?? 'md',
    type: widget?.config?.type ?? 'static',
    channel: widget?.config?.channel ?? 'email',
    progressBarColor: widget?.config?.progressBarColor ?? '',
    backgroundColor: widget?.config?.backgroundColor ?? '',
    label: widget?.config?.label ?? 'Donate',
    labelColor: widget?.config?.labelColor ?? '#FFFFFF',
    borderColor: widget?.config?.borderColor ?? 'transparent',
    borderRadius: [widget?.config?.borderRadius ? parseInt(widget.config.borderRadius) : 24],
    borderWidth: [widget?.config?.borderWidth ? parseInt(widget.config.borderWidth) : 0],
    status: widget?.status ?? 'active',
    position: widget?.config?.position ?? 'bottom-right',
    icon: widget?.config?.icon ?? 'heart',
    iconPosition: widget?.config?.iconPosition ?? 'left',
    dropShadow: widget?.config?.dropShadow ?? true,
    showGoalAmount: widget?.config?.showGoalAmount ?? true,
    showRaisedAmount: widget?.config?.showRaisedAmount ?? true,
    showRaisedPercentage: widget?.config?.showRaisedPercentage ?? true,
    showGoalBar: widget?.config?.showGoalBar ?? false,
    buttonText: widget?.config?.buttonText ?? 'Subscribe',
    buttonBackgroundColor: widget?.config?.buttonBackgroundColor ?? '#35B254',
    buttonTextColor: widget?.config?.buttonTextColor ?? '#FFFFFF',
    openDelay: [widget?.config?.openDelay ? parseInt(widget.config.openDelay) : 5000],
    image: widget?.config?.image ? [{ id: 'image', url: widget.config.image }] : [],
    imageSizing: widget?.config?.imageSizing ?? 'stretch',
    isFirstAndLastNameRequired: widget?.config?.isFirstAndLastNameRequired ?? true,
});

const getDefaultCampaign = (campaigns: Campaign[] = []) => {
    let defaultCampaign: Campaign | undefined;
    defaultCampaign = campaigns.find(campaign => campaign.type === 'general');

    if (!defaultCampaign) {
        defaultCampaign = campaigns.length > 0 ? campaigns[0] : undefined;
    }

    return defaultCampaign;
};

export { transformWidgetIntoFormValues };
